import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ToolGrid } from '@/components/tools/ToolGrid';
import { ToolFilters } from '@/components/tools/ToolFilters';
import { useTools } from '@/hooks/useTools';
import { useUser } from '@/hooks/useUser';
import { supabase } from '@/lib/supabase';
import { Tool } from '@/types/database';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Wrench,
  Radio,
  Archive,
  FileText,
  Layers,
  AlertTriangle,
  ArrowRight,
  Plus,
  RefreshCw,
} from 'lucide-react';

// ── Admin pipeline status panel ───────────────────────────────────────────────

interface PipelineStats {
  sources: number;
  artifacts: number;
  pending_jobs: number;
  running_jobs: number;
  failed_jobs: number;
}

function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [
        { count: sources },
        { count: artifacts },
        { count: pending },
        { count: running },
        { count: failed },
      ] = await Promise.all([
        supabase.from('sources').select('*', { count: 'exact', head: true }),
        supabase.from('artifacts').select('*', { count: 'exact', head: true }),
        supabase
          .from('worker_jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('worker_jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'running'),
        supabase
          .from('worker_jobs')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'failed'),
      ]);
      setStats({
        sources: sources ?? 0,
        artifacts: artifacts ?? 0,
        pending_jobs: pending ?? 0,
        running_jobs: running ?? 0,
        failed_jobs: failed ?? 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchStats();
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold text-gray-900">Ingestion Pipeline</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void fetchStats()}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <Link
            to="/sources"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Source
          </Link>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        {[
          {
            label: 'Sources',
            value: stats?.sources,
            icon: <Radio className="w-4 h-4 text-blue-500" />,
            href: '/sources',
          },
          {
            label: 'Artifacts',
            value: stats?.artifacts,
            icon: <Archive className="w-4 h-4 text-purple-500" />,
            href: '/archive',
          },
          {
            label: 'Pending jobs',
            value: stats?.pending_jobs,
            icon: <Layers className="w-4 h-4 text-yellow-500" />,
            href: null,
          },
          {
            label: 'Running',
            value: stats?.running_jobs,
            icon: <FileText className="w-4 h-4 text-green-500" />,
            href: null,
          },
          {
            label: 'Failed',
            value: stats?.failed_jobs,
            icon: <AlertTriangle className="w-4 h-4 text-red-500" />,
            href: null,
            alert: (stats?.failed_jobs ?? 0) > 0,
          },
        ].map((tile) => (
          <div
            key={tile.label}
            className={`rounded-lg border p-3 flex flex-col gap-1 ${
              tile.alert
                ? 'bg-red-50 border-red-200'
                : 'bg-gray-50 border-gray-200'
            } ${tile.href ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors' : ''}`}
            onClick={() => tile.href && navigate(tile.href)}
          >
            <div className="flex items-center justify-between">
              {tile.icon}
              {tile.href && <ArrowRight className="w-3 h-3 text-gray-400" />}
            </div>
            <p
              className={`text-xl font-bold ${tile.alert ? 'text-red-700' : 'text-gray-900'}`}
            >
              {loading ? '—' : (tile.value ?? 0)}
            </p>
            <p
              className={`text-xs ${tile.alert ? 'text-red-600' : 'text-gray-500'}`}
            >
              {tile.label}
            </p>
          </div>
        ))}
      </div>

      {/* Pipeline steps */}
      <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap">
        {[
          'Add Source',
          'Scrape',
          'Extract Text',
          'Embed (Search)',
          'Analyze',
        ].map((step, i, arr) => (
          <React.Fragment key={step}>
            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-gray-700 font-medium">
              {step}
            </span>
            {i < arr.length - 1 && (
              <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
            )}
          </React.Fragment>
        ))}
        <span className="ml-1 text-gray-400">
          (auto-runs after each scrape)
        </span>
      </div>
    </div>
  );
}

export const DashboardPage: React.FC = () => {
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const { tools, loading, error, fetchTools } = useTools();
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const isSysAdmin = user?.role === 'SYSTEM_ADMIN';

  // Fetch tools on mount
  useEffect(() => {
    fetchTools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter tools based on search and filters (computed value, no effect needed)
  const filteredTools = React.useMemo(() => {
    const DASHBOARD_TOOL_ORDER = [
      'on-the-record',
      'document-qa',
      'full-text-search',
      'knowledge-explorer',
      'topic-monitor',
      'deep-research-brief',
      'meeting-summary',
      'platform-guide',
    ];

    let result = [...tools];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (tool) =>
          tool.name.toLowerCase().includes(query) ||
          (tool.description?.toLowerCase() || '').includes(query)
      );
    }

    // Category filter (not implemented yet)
    if (selectedCategory) {
      // TODO: Add category field to Tool type when available
    }

    // Type filter
    if (selectedType) {
      result = result.filter((tool) => tool.type === selectedType);
    }

    // Sort to the requested dashboard order; anything not in the list stays at the end
    const orderMap = new Map(DASHBOARD_TOOL_ORDER.map((s, i) => [s, i]));
    result.sort((a, b) => {
      const ai = orderMap.get(a.slug) ?? Infinity;
      const bi = orderMap.get(b.slug) ?? Infinity;
      return ai - bi;
    });

    return result;
  }, [tools, searchQuery, selectedCategory, selectedType]);

  // Get unique categories from tools
  const categories: string[] = [];
  // TODO: Implement categories when category field is added to Tool type

  const handleToolClick = (tool: Tool) => {
    navigate(`/tools/${tool.slug}`);
  };

  const handleToolExecute = (tool: Tool) => {
    navigate(`/tools/${tool.slug}?execute=true`);
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <Wrench className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Failed to load tools
        </h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchTools}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Access and execute AI-powered tools for your workflows
        </p>
      </div>

      {/* Admin Ingestion Pipeline Panel — visible to SYSTEM_ADMIN only */}
      {isSysAdmin && <AdminPanel />}

      {/* Filters */}
      <ToolFilters
        onSearch={setSearchQuery}
        onCategoryChange={setSelectedCategory}
        onTypeChange={setSelectedType}
        categories={categories}
      />

      {/* Tools Grid */}
      <ToolGrid
        tools={filteredTools}
        onExecute={handleToolExecute}
        onClick={handleToolClick}
        loading={loading}
        emptyMessage={
          searchQuery || selectedCategory || selectedType
            ? 'No tools match your filters'
            : 'No tools available'
        }
      />
    </div>
  );
};
